<!--
  ClientsEmptyState.vue -- focused first-Client CTA.

  The Distributor has zero Clients. Don't render an empty table or a
  bland placeholder; surface a clear call to action that drives toward
  the next legitimate step ("invite your first client").

  When the caller doesn't hold ``client.invite_admin``, we fall back to
  a read-only explanation -- a Builder shouldn't see an invite button
  they can't use.

  Distributor-context gate (audit F3, 2026-06-24): "Invite a Client"
  creates a brand-new Client row, which is inherently distributor-scoped
  (clients.distributor_id is NOT NULL). A Super Admin who is NOT
  impersonating has no tenant to scope the new Client to -- the backend
  rejects POST /clients/ with a clean 400. Hide the CTA in that state
  and surface a gated note explaining the impersonation requirement.
  Mirrors the gate pattern in StoreClientContact.vue (PR-R, #1679).
-->
<template>
    <div class="ces" data-testid="clients-workspace-empty-state">
        <h3 class="ces__title">No clients yet</h3>
        <p class="ces__body">
            <template v-if="canInviteEffective">
                Invite your first client to start building their store.
            </template>
            <template v-else-if="canInvite && !hasDistributorContext">
                Impersonate a distributor to invite the first Client.
            </template>
            <template v-else>
                When your team invites a client, they'll appear here.
            </template>
        </p>
        <button
            v-if="canInviteEffective"
            type="button"
            class="ces__cta"
            data-testid="clients-workspace-empty-cta"
            @click="$emit('invite')"
        >
            Invite a Client
        </button>
        <p
            v-else-if="canInvite && !hasDistributorContext"
            class="ces__gated-note"
            data-testid="clients-workspace-empty-gated-note"
        >
            Use the SuperAdmin "Impersonate" action on a distributor to
            create their first Client.
        </p>
    </div>
</template>

<script>
import { useImpersonationStore } from '@/stores/impersonation'

export default {
    name: 'ClientsEmptyState',
    props: {
        canInvite: { type: Boolean, default: false },
    },
    emits: ['invite'],
    computed: {
        impersonationStore() {
            return useImpersonationStore()
        },
        isSuperAdmin() {
            return Boolean(this.$store && this.$store.getters.isSuperAdmin)
        },
        // True for any non-SA principal (Owner / Builder always have a
        // tenant), or for an SA who IS impersonating a distributor.
        // Mirrors the SaImpersonationBanner predicate and the
        // ClientsListPage / StoreClientContact gate.
        hasDistributorContext() {
            return !this.isSuperAdmin || this.impersonationStore.isActive
        },
        // Effective gate for the CTA: caller must hold the capability
        // AND have a distributor context to scope the new Client to.
        canInviteEffective() {
            return this.canInvite && this.hasDistributorContext
        },
    },
}
</script>

<style scoped>
.ces {
    padding: 48px 24px;
    text-align: center;
    background: var(--tf-bg, #fff);
    border: 1px dashed var(--tf-border, #d0d4dc);
    border-radius: var(--tf-radius-sm, 6px);
    color: var(--tf-fg-2);
}
.ces__title {
    margin: 0 0 8px;
    font-size: 18px;
    font-weight: 600;
    color: var(--tf-fg-1);
}
.ces__body {
    margin: 0 0 20px;
    font-size: 14px;
    line-height: 1.4;
}
.ces__cta {
    padding: 10px 24px;
    border: 1px solid var(--tf-accent, #2c5fff);
    background: var(--tf-accent, #2c5fff);
    color: #fff;
    font-size: 14px;
    border-radius: var(--tf-radius-xs, 4px);
    cursor: pointer;
}
.ces__cta:hover {
    filter: brightness(0.95);
}
.ces__gated-note {
    margin: 0;
    font-size: 13px;
    color: var(--tf-fg-3);
    max-width: 360px;
    margin-left: auto;
    margin-right: auto;
}
</style>
